import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api-client';
import { VideoPlayer } from './VideoPlayer';

interface Playlist {
  id: string;
  name: string;
  type: 'm3u' | 'xtream';
  source_key: string;
  created_at: string;
  updated_at: string;
  channel_count: number;
}

interface PlaylistsResponse {
  playlists: Playlist[];
}

interface Channel {
  id: string;
  name: string;
  category?: string;
  xtream_stream_id?: string;
  stream_url?: string;
}

interface ChannelsResponse {
  channels: Channel[];
  count: number;
}

interface CategoryGroup {
  name: string;
  channels: Channel[];
}

export function PlaylistList() {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [browsingPlaylistId, setBrowsingPlaylistId] = useState<string | null>(null);
  const [browsingPlaylistName, setBrowsingPlaylistName] = useState<string | null>(null);
  const [browsingChannels, setBrowsingChannels] = useState<Channel[]>([]);
  const [browsingLoading, setBrowsingLoading] = useState(false);
  const [browsingError, setBrowsingError] = useState<string | null>(null);
  const [browsingSearch, setBrowsingSearch] = useState('');
  const [browsingCategory, setBrowsingCategory] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlaylists = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiFetch<PlaylistsResponse>('/playlists');
        setPlaylists(result.playlists || []);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch playlists';
        setError(message);
        console.error('Error fetching playlists:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlaylists();
  }, []);

  if (loading) {
    return <div className="playlist-list"><p>Loading playlists...</p></div>;
  }

  if (error) {
    return <div className="playlist-list error"><p>Error: {error}</p></div>;
  }

  if (playlists.length === 0) {
    return (
      <div className="playlist-list">
        <p>No playlists yet. <a href="#add">Create one</a>.</p>
      </div>
    );
  }

  return (
    <div className="playlist-list">
      {showPlayer && selectedChannel && (
        <VideoPlayer
          channelId={selectedChannel.id}
          channelName={selectedChannel.name}
          onClose={() => {
            setShowPlayer(false);
            setSelectedChannel(null);
          }}
        />
      )}

      {browsingPlaylistId && (
        <div className="channel-browser-modal">
          <div className="channel-browser-container">
            <div className="channel-browser-header">
              <h3>{browsingPlaylistName}</h3>
              <button className="close-btn" onClick={() => {
                setBrowsingPlaylistId(null);
                setBrowsingSearch('');
                setBrowsingCategory(null);
              }}>✕</button>
            </div>

            {browsingLoading && <div className="channel-browser-loading">Loading channels...</div>}
            {browsingError && <div className="channel-browser-error">Error: {browsingError}</div>}

            {!browsingLoading && browsingChannels.length > 0 && (
              <>
                <div className="channel-browser-controls">
                  <input
                    type="text"
                    placeholder="Search channels..."
                    value={browsingSearch}
                    onChange={(e) => setBrowsingSearch(e.target.value)}
                    className="channel-search-input"
                  />
                </div>

                {(() => {
                  const categories = Array.from(new Set(browsingChannels.map(c => c.category || 'Uncategorized')));
                  const filtered = browsingChannels.filter(c => {
                    const matchesSearch = c.name.toLowerCase().includes(browsingSearch.toLowerCase());
                    const matchesCategory = !browsingCategory || (c.category || 'Uncategorized') === browsingCategory;
                    return matchesSearch && matchesCategory;
                  });

                  return (
                    <>
                      {categories.length > 1 && (
                        <div className="channel-browser-tabs">
                          <button
                            className={`channel-tab ${!browsingCategory ? 'active' : ''}`}
                            onClick={() => setBrowsingCategory(null)}
                          >
                            All ({browsingChannels.length})
                          </button>
                          {categories.map(cat => (
                            <button
                              key={cat}
                              className={`channel-tab ${browsingCategory === cat ? 'active' : ''}`}
                              onClick={() => setBrowsingCategory(cat)}
                            >
                              {cat}
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="channel-browser-grid">
                        {filtered.length === 0 ? (
                          <div className="channel-browser-empty">
                            {browsingSearch ? 'No channels match your search' : 'No channels in this category'}
                          </div>
                        ) : (
                          filtered.map((channel) => (
                            <div
                              key={channel.id}
                              className="channel-card"
                              onClick={() => {
                                setSelectedChannel(channel);
                                setShowPlayer(true);
                                setBrowsingPlaylistId(null);
                              }}
                            >
                              <div className="channel-card-content">
                                <div className="channel-card-name">{channel.name}</div>
                                {channel.category && (
                                  <div className="channel-card-category">{channel.category}</div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            {!browsingLoading && !browsingError && browsingChannels.length === 0 && (
              <div className="channel-browser-empty">No channels found</div>
            )}
          </div>
        </div>
      )}

      <table className="playlists-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Channels</th>
            <th>Created</th>
            <th>Updated</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {playlists.map((playlist) => (
            <tr key={playlist.id}>
              <td className="name-cell">
                <strong>{playlist.name}</strong>
              </td>
              <td className="type-cell">
                <span className={`badge badge-${playlist.type}`}>
                  {playlist.type.toUpperCase()}
                </span>
              </td>
              <td className="channels-cell">
                <strong>{playlist.channel_count}</strong>
              </td>
              <td className="date-cell">
                {new Date(playlist.created_at).toLocaleDateString()}
              </td>
              <td className="date-cell">
                {new Date(playlist.updated_at).toLocaleDateString()}
              </td>
              <td className="action-cell">
                <button
                  className="play-btn"
                  onClick={async () => {
                    setBrowsingPlaylistId(playlist.id);
                    setBrowsingPlaylistName(playlist.name);
                    setBrowsingLoading(true);
                    setBrowsingError(null);
                    try {
                      const result = await apiFetch<ChannelsResponse>(`/playlists/${playlist.id}/channels`);
                      setBrowsingChannels(result.channels || []);
                    } catch (err) {
                      const message = err instanceof Error ? err.message : 'Failed to fetch channels';
                      setBrowsingError(message);
                      console.error('Error fetching channels:', err);
                    } finally {
                      setBrowsingLoading(false);
                    }
                  }}
                >
                  Browse
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
